import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PaymentLayout } from '../../paymentLayout/PaymentLayout';
import { useAuth } from '../../../context/AuthContext';
import { TicketModal } from '../TicketModal/TicketModal';
import { QuickSignupModal } from '../QuickSignupModal/QuickSignupModal';
import { PhoneUpdateModal } from '../PhoneUpdateModal/PhoneUpdateModal';
import { RAZORPAY_KEY_ID } from '../../../config/razorpayConfig';
import './PayButton.css';

import { BASE_URL } from '../../../config/config';

const API_BASE_URL = BASE_URL;

const MultiPayButton = ({
  userId: propUserId,
  cartItems,
  totalAmount,
  onBookingSuccess,
  onBookingFailure,
  disabled = false,
}) => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [newUserId, setNewUserId] = useState(null);
  const [multiBookingData, setMultiBookingData] = useState(null);

  // Get the active user ID
  const userId = newUserId || propUserId || user?.result?._id;

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const bookingsRef = React.useRef(null);

  const openRazorpayCheckout = async (orderInfo) => {
    bookingsRef.current = orderInfo.bookings;
    const isLoaded = await loadRazorpayScript();
    if (!isLoaded) {
      alert('Razorpay SDK failed to load. Please check your connection.');
      return;
    }

    const options = {
      key: RAZORPAY_KEY_ID,
      amount: orderInfo.amount,
      currency: 'INR',
      name: 'Hobi App',
      description: 'Multiple Bookings',
      order_id: orderInfo.orderId,
      handler: function (response) {
        console.log('Multi Payment Success:', response);
        handlePaymentSuccess({
          ...response,
          transactionId: response.razorpay_payment_id,
        }, orderInfo.bookings);
      },
      prefill: {
        name: user?.result?.full_name || '',
        email: user?.result?.email || '',
        contact: user?.result?.phone || '',
      },
      theme: {
        color: '#ff7e5f',
      },
      modal: {
        ondismiss: function () {
          console.log('Multi Payment dismissed');
          handlePaymentFailure({ reason: 'dismissed' });
        },
      },
    };

    const razorpay = new window.Razorpay(options);
    razorpay.on('payment.failed', function (response) {
      console.log('Multi Payment Failed:', response.error);
      handlePaymentFailure(response.error);
    });
    razorpay.open();
  };

  const createMultiBooking = async (userIdToUse) => {
    setLoading(true);
    try {
      const receiptId = `receipt_${Date.now()}`;
      const items = cartItems.map(item => ({
        eventId: item.eventId,
        ticketId: item.ticketId,
        ticketsCount: item.ticketsCount,
        targetMonth: item.targetMonth || null
      }));

      const response = await fetch(`${API_BASE_URL}/bookings/create-multiple-bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: userIdToUse,
          receipt: receiptId,
          items: items,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create multiple bookings');
      }

      // data.payment should contain the Razorpay order info for the total amount
      // data.result should contain the array of bookings created
      
      const orderInfo = {
        orderId: data.payment.id,
        amount: data.payment.amount,
        bookings: data.result, // Array of booking objects
      };
      
      setMultiBookingData(orderInfo);
      setLoading(false);
      return orderInfo;
    } catch (error) {
      console.error('Multi booking creation failed:', error);
      alert(error.message || 'Failed to create bookings. Please try again.');
      if (onBookingFailure) {
        onBookingFailure(error);
      }
      setLoading(false);
      return null;
    }
  };

  const handleInitiateMultiBooking = async () => {
    if (!isAuthenticated && !newUserId) {
      setShowSignupModal(true);
      return null;
    }

    if (isAuthenticated && !user?.result?.phone && !newUserId) {
      setShowPhoneModal(true);
      return null;
    }
    
    const orderInfo = await createMultiBooking(userId);
    if (orderInfo) {
      await openRazorpayCheckout(orderInfo);
    }
  };

  const handlePaymentSuccess = async (response, bookingsFromOrder) => {
    try {
      const bookingsToUse = bookingsFromOrder || bookingsRef.current || multiBookingData?.bookings;
      
      if (!bookingsToUse) {
        throw new Error('No booking data found for confirmation');
      }

      // Prepare updates array for confirm-multiple-bookings
      const updates = bookingsToUse.map(booking => {
        // Find corresponding cart item to get platform fee
        const cartItem = cartItems.find(item => item.ticketId === booking.ticketId);
        return {
          bookingId: booking._id,
          platformfee: cartItem?.platformFee || 0
        };
      });

      const updateResponse = await fetch(`${API_BASE_URL}/bookings/confirm-multiple-bookings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transactionId: response.transactionId,
          updates: updates
        }),
      });
      const data = await updateResponse.json();

      if (!updateResponse.ok) {
        throw new Error(data.message || 'Failed to confirm multiple bookings');
      }

      if (onBookingSuccess) {
        onBookingSuccess(data);
      }
      setMultiBookingData(null);
      bookingsRef.current = null;
      navigate('/profile');
    } catch (error) {
      console.error('Multi booking confirmation failed:', error);
      alert('Payment received but booking updates failed. Please contact support.');
    }
  };

  const handlePaymentFailure = (error) => {
    console.log('Multi Payment failed:', error);
    setMultiBookingData(null);
    bookingsRef.current = null;
    if (onBookingFailure) {
      onBookingFailure(error);
    }
  };

  const handleSignupSuccess = async (response) => {
    setShowSignupModal(false);
    const signupUserId = response?.result?._id;
    if (signupUserId) {
      setNewUserId(signupUserId);
      const orderInfo = await createMultiBooking(signupUserId);
      if (orderInfo) {
        await openRazorpayCheckout(orderInfo);
      }
    }
  };

  const handlePhoneUpdateSuccess = async () => {
    setShowPhoneModal(false);
    const orderInfo = await createMultiBooking(userId);
    if (orderInfo) {
      await openRazorpayCheckout(orderInfo);
    }
  };

  return (
    <>
      <a 
        href="/#" 
        className="button-3 w-button" 
        onClick={(e) => {
          e.preventDefault();
          handleInitiateMultiBooking();
        }}
        style={{
          filter: (loading || disabled) ? 'grayscale(1)' : 'none',
          opacity: (loading || disabled) ? 0.7 : 1,
          pointerEvents: (loading || disabled) ? 'none' : 'auto',
          cursor: (loading || disabled) ? 'not-allowed' : 'pointer',
          width: '100%',
          textAlign: 'center',
          marginTop: '20px'
        }}
      >
        {loading ? 'Processing...' : `Pay ₹${totalAmount}`}
      </a>

      <QuickSignupModal
        isOpen={showSignupModal}
        onClose={() => setShowSignupModal(false)}
        onSuccess={handleSignupSuccess}
      />

      <PhoneUpdateModal 
        isOpen={showPhoneModal}
        onClose={() => setShowPhoneModal(false)}
        onSuccess={handlePhoneUpdateSuccess}
      />
    </>
  );
};

export default MultiPayButton;
